import { ErrorEvent, EventHint } from '@sentry/types';
import { getStorageEstimate } from './storageEstimateUtils';
import * as Sentry from '@sentry/react';
import { LOGGER_ENABLED, sendSentryEventsAsLogs } from '@upheal/utils/logger';
import { BROWSER_SESSION_ID } from '@upheal/utils/browserSession';
import { COLLECTOR_URL } from './analytics/snowplow';
import { BrowserTracing } from '@sentry/tracing';
import escapeStringRegexp from 'escape-string-regexp';
import { UPHEAL_API_URL, UPHEAL_GRAPHQL_API_URL } from './configuration';
import { SentryAutogroupError, TAG_EMPTY_VALUE } from './sentry';
import { featureFlagsState } from '@upheal/utils/feature-flags';

// based on https://gist.github.com/impressiver/5092952
const ignoreErrors = [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    /.*@webkit-masked-url.*/,
];

// based on https://gist.github.com/impressiver/5092952
const denyUrls = [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Any extension excluding our own
    RegExp(
        `^chrome-extension://(?!${import.meta.env.VITE_EXTENSION_STORE_ID})`,
        'i',
    ),
    // Firefox extensions
    /^resource:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
];

// Some errors coming from TipTap may expose editor content, we need to mask it not to leak sensitive data
const maskEditorContent = (event: ErrorEvent, hint: EventHint) => {
    const { originalException } = hint;
    if (!(originalException instanceof RangeError)) {
        return;
    }
    const sensitiveMessagesRegexes = [
        /^(Position \d+ outside of fragment) (.*)/,
        /^(Index \d+  out of range for) (.*)/,
    ];
    const sensitiveMessageRegex = sensitiveMessagesRegexes.find((regex) =>
        originalException.message.match(regex),
    );

    if (sensitiveMessageRegex) {
        const maskedMessage = originalException.message.replace(
            sensitiveMessageRegex,
            '$1 <masked>',
        );

        if (event.exception?.values?.[0]) {
            event.exception.values[0].value = maskedMessage;
        }
    }

    return event;
};

const addErrorInfoTags = async (event: ErrorEvent) => {
    const error = event.extra?.error as Record<string, any> | undefined;
    const tags = event.tags || {};

    if (error?.code && !tags.error_code) {
        tags.error_code = error.code;
    }

    const errorMessage =
        (typeof error === 'string' && error) ||
        error?.message ||
        error?.errorMsg;
    if (errorMessage && !tags.error_message) {
        tags.error_message = errorMessage;
    }

    if (error?.name && !tags.error_name) {
        tags.error_name = error.name;
    }

    event.tags = tags;
    return event;
};

const addBatteryInfoTags = async (event: ErrorEvent) => {
    const tags = event.tags || {};

    try {
        if (
            ['fatal', 'error'].includes(event.level as string) &&
            window.navigator.getBattery
        ) {
            const battery = await window.navigator.getBattery();
            tags.battery_discharging_time = battery.dischargingTime;
            tags.battery_level = battery.level;
        }
    } catch {
        // ignore
    }

    event.tags = tags;
    return event;
};

const addStorageInfo = async (event: ErrorEvent) => {
    const tags = event.tags || {};
    const extra = event.extra || {};

    const storage = await getStorageEstimate();

    if (storage) {
        extra.storageEstimate = {
            quota: storage.quota,
            usage: storage.usage,
        };

        tags.storage_usage_ratio = storage.storageUsageRatio;
    }

    event.tags = tags;
    event.extra = extra;
    return event;
};

const addDeviceHardwareInfo = async (event: ErrorEvent) => {
    const tags = event.tags || {};

    try {
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/deviceMemory
        const deviceMemoryInMB =
            ((window.navigator as any)?.deviceMemory ?? 0) * 1024;

        if (deviceMemoryInMB) {
            tags.device_memory_mb = deviceMemoryInMB;
        }

        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/hardwareConcurrency
        tags.number_of_processors = window.navigator.hardwareConcurrency;
    } catch {
        // ignore
    }

    event.tags = tags;
    return event;
};

const networkErrorNames = ['AxiosError', 'ApolloError', 'NetworkError'];
const adjustEventSeverity = (event: ErrorEvent) => {
    if (
        event.level === 'error' &&
        typeof event.tags?.error_name === 'string' &&
        networkErrorNames.includes(event.tags.error_name)
    ) {
        event.level = 'warning';
    }
};

const resolveAutogrouping = (event: ErrorEvent, hint: EventHint) => {
    const { originalException } = hint;
    if (originalException instanceof SentryAutogroupError) {
        event.fingerprint = [
            originalException.message,
            import.meta.env.VITE_SENTRY_WEBAPP_BUILD_TARGET,
            event.level ?? '',
        ];
    }
};

const addFeatureFlags = (event: ErrorEvent) => {
    const tags = event.tags || {};

    Object.entries(featureFlagsState).forEach(([key, value]) => {
        tags[`ff_${key}`] = value;
    });

    event.tags = tags;
};

export const initSentry = () => {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_PROJECT_DNS,
        enabled: LOGGER_ENABLED,
        environment: import.meta.env.VITE_REACT_APP_SERVER_STAGE,
        release:
            import.meta.env.VITE_REACT_APP_ENABLE_SENTRY_RELEASE === '1'
                ? import.meta.env.VITE_REACT_APP_GIT_COMMIT_ID
                : undefined,

        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
        ignoreErrors,
        denyUrls,

        // For WebApp, let's capture 10% of transactions, quota is 100k/month
        // For Browser extension, don't capture it, as main-panel.html runs in context of all opened tabs, it eats up quota quickly and the value is questionable
        tracesSampleRate:
            import.meta.env.VITE_REACT_APP_IS_EXTENSION === 'true' ? 0 : 0.1,

        beforeSend: async (event, hint) => {
            maskEditorContent(event, hint);
            await addErrorInfoTags(event);
            await addBatteryInfoTags(event);
            await addStorageInfo(event);
            await addDeviceHardwareInfo(event);
            adjustEventSeverity(event);

            sendSentryEventsAsLogs(event, hint);
            resolveAutogrouping(event, hint);
            addFeatureFlags(event);

            return event;
        },

        initialScope: (scope) => {
            scope.setTag('branch', import.meta.env.VITE_REACT_APP_GIT_BRANCH);
            scope.setTag('browser_session', BROWSER_SESSION_ID);

            // it's better to always have *some* value, because Sentry doesn't support filtering "only missing values"
            scope.setTag('client_id', TAG_EMPTY_VALUE);
            scope.setTag('session_id', TAG_EMPTY_VALUE);
            scope.setTag('recording_id', TAG_EMPTY_VALUE);
            scope.setTag('stream_recording_id', TAG_EMPTY_VALUE);

            return scope;
        },
        maxBreadcrumbs: 200,

        beforeBreadcrumb(breadcrumb) {
            if (
                breadcrumb.data?.url?.includes('betterstack') ||
                breadcrumb.data?.url?.includes(COLLECTOR_URL)
            ) {
                return null;
            }

            return breadcrumb;
        },

        // https://docs.sentry.io/platforms/javascript/session-replay/#sampling
        // Do not record on production by default. For cases when we want to record, we will use startSentryReplay()
        replaysSessionSampleRate:
            import.meta.env.MODE === 'production' ? 0 : 1.0,
        // Even though we don't record replays in production, the buffer is still on and when error occurs
        // 1 min before error and then rest of the session is recorded and sent to Sentry.
        replaysOnErrorSampleRate: 1.0,

        integrations:
            import.meta.env.VITE_REACT_APP_IS_EXTENSION === 'true'
                ? [new BrowserTracing()]
                : [
                      new BrowserTracing(),
                      new Sentry.Replay({
                          stickySession: false, // when `true` opening internal link in a new tab doesn't behave as expected
                          minReplayDuration: 15000,

                          // https://docs.sentry.io/platforms/javascript/session-replay/privacy/#privacy-configuration
                          maskAllText: true,
                          maskAllInputs: true,

                          blockAllMedia: false,
                          block: [
                              'img',
                              // "svg", // Do not block SVG for now, as all icons&charts would be blocked
                              'video',
                              'object',
                              'picture',
                              'embed',
                              'map',
                              'audio',
                              '.sentry-block',
                              '[data-sentry-block]',
                          ],

                          networkDetailAllowUrls: [
                              // Allow all requests to our own API
                              new RegExp(
                                  `^${escapeStringRegexp(UPHEAL_API_URL)}`,
                              ),
                              new RegExp(
                                  `^${escapeStringRegexp(
                                      UPHEAL_GRAPHQL_API_URL,
                                  )}`,
                              ),
                          ],
                          networkCaptureBodies: false,
                          networkRequestHeaders: [
                              'correlation-id',
                              'frontend-session-id',
                              'X-Nonce',
                          ],
                      }),
                  ],
    });
};
